import { useState } from 'react'
import { AmountCard, Button, EmptyState, ExplainerBanner, Sheet, Skeleton } from '@/ds'
import { useLoadedStay, useStay } from '@/app/stay/StayProvider'
import { formatMoney, listJoin } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { isServiceError } from '@/services/errors'
import { extrasService } from '@/services/extras'
import type { CartItem, Extra } from '@/services/extras'
import { pagosService, writePendingPayment } from '@/services/pagos'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { CardChoice, choiceErrors, chosenLast4, initialChoice, toCardInput } from '../Pagos/CardChoice'
import type { CardChoiceValue } from '../Pagos/CardChoice'
import { BackLink, PAY_ROUTES, paymentErrorMessage } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'
import { amountText, cartLineLabel, EXTRAS_ROUTES, lineAmount, readCart, writeCart, writeExtrasResult } from './shared'

/**
 * 8.6 · Confirmar y pagar (Servicios.dc.html cart, Bloque 6 §6b). Desglose con fecha y personas, "Gratis" en verde e
 * importe exacto en el botón.
 * - Mock (contrato propuesto): tarjeta guardada u otra (D10), condiciones y cobro con el banco del bloque 3.
 * - API actual (D46): E10 por extra; el PMS cobra fuera de la app, y se dice con el texto de la app actual.
 */
export function ExtrasConfirmar() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [cart] = useState(() => readCart(stay.guestCode))
  const [data, reload] = useAsync(`extras:${stay.guestCode}`, (signal) => extrasService.list(stay, signal))
  const extras = asyncValue(data)
  const items = extras ? cart.flatMap((c) => (extras.some((e) => e.id === c.extraId && e.kind === 'extra' && !e.alreadyAdded) ? [c] : [])) : []
  const paysInApp = extrasService.paysInApp
  const title = paysInApp ? t('v2.extras.confirmar.titulo') : t('v2.extras.confirmar.tituloReal')

  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to={EXTRAS_ROUTES.catalog} label={t('v2.extras.confirmar.volver')} />
        <h1>{title}</h1>
        {extras && items.length > 0 && <p className="page__lead">{paysInApp ? t('v2.extras.confirmar.lead') : t('v2.extras.confirmar.leadReal')}</p>}
      </div>
      {data.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={280} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {extras && items.length === 0 && <EmptyCart />}
      {extras && items.length > 0 && <Checkout extras={extras} items={items} />}
    </main>
  )
}

function EmptyCart() {
  const { t } = useLang()
  const { go } = useAppNavigate()
  return (
    <EmptyState icon="shopping-bag" title={t('v2.extras.confirmar.vacioTitulo')} headingLevel={2} actionLabel={t('v2.extras.confirmar.verExtras')} onAction={() => go(EXTRAS_ROUTES.catalog, { replace: true })}>
      {t('v2.extras.confirmar.vacioTexto')}
    </EmptyState>
  )
}

function Checkout({ extras, items }: { extras: Extra[]; items: CartItem[] }) {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const { replaceStay } = useStay()
  const { go } = useAppNavigate()
  const paysInApp = extrasService.paysInApp
  const rows = items.flatMap((item) => {
    const extra = extras.find((e) => e.id === item.extraId)
    return extra ? [{ item, extra, amount: lineAmount(extra, item.quantity) }] : []
  })
  const total = { amount: rows.reduce((sum, r) => sum + r.amount.amount, 0), currency: stay.currency }
  const totalText = formatMoney(total, lang)
  const needsCard = paysInApp && total.amount > 0

  const [summary, reloadSummary] = useAsync(`pagos:${stay.guestCode}:${apiLang}:extras`, (signal) => (needsCard ? pagosService.getPaymentSummary(stay, apiLang, signal) : Promise.resolve(null)))
  const s = asyncValue(summary)
  const [choice, setChoice] = useState<CardChoiceValue | null>(null)
  const current = choice ?? (s ? initialChoice(s) : null)
  const [showErrors, setShowErrors] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const conditions = rows.flatMap((r) => (r.extra.conditions ? [`${r.extra.name}: ${r.extra.conditions}`] : []))

  const [confirm, setConfirm] = useState(false)
  // Confirmación de dinero siempre en hoja con el resumen y el importe exacto (DESIGN.md §4, fase 4 A4).
  const review = () => {
    setError(null)
    if (needsCard) {
      setShowErrors(true)
      if (!s || !current || Object.keys(choiceErrors(s, current)).length > 0) return
    }
    setConfirm(true)
  }
  const confirmLabel = paysInApp && total.amount > 0 ? t('v2.extras.confirmar.pagar', { amount: totalText }) : t('v2.legacy.preview.labels.availableExtrasView.confirm')
  const last4 = needsCard && s && current ? chosenLast4(s, current) : null

  const submit = async () => {
    setError(null)
    setBusy(true)
    try {
      const res = await extrasService.checkout(stay, items, needsCard && s && current ? toCardInput(s, current) : null)
      if (res.mode === 'added') {
        if (res.stay) replaceStay(res.stay)
        const pending = items.filter((i) => i.extraId === res.failed || res.notTried.includes(i.extraId))
        writeCart(stay.guestCode, pending)
        writeExtrasResult({ guestCode: stay.guestCode, mode: 'added', added: res.added, failed: res.failed, notTried: res.notTried })
        go(EXTRAS_ROUTES.done, { replace: true })
      } else if (res.mode === 'confirmed') {
        writeCart(stay.guestCode, [])
        writeExtrasResult({ guestCode: stay.guestCode, mode: 'paid', checkoutId: res.checkoutId })
        go(EXTRAS_ROUTES.done, { replace: true })
      } else {
        writeExtrasResult({ guestCode: stay.guestCode, mode: 'paid', checkoutId: res.checkoutId })
        writePendingPayment({ guestCode: stay.guestCode, kind: 'extras', checkoutId: res.checkoutId, amount: res.amount.amount, currency: res.amount.currency, temporalPaidId: res.temporalPaidId, iframeUrl: res.iframeUrl, returnTo: EXTRAS_ROUTES.done, retryTo: EXTRAS_ROUTES.confirm, startedAt: new Date().toISOString() })
        go(PAY_ROUTES.authorize, { state: { last4: s && current ? chosenLast4(s, current) : null } })
      }
    } catch (e) {
      if (isServiceError(e) && e.code === 'payment_failed') setError(paymentErrorMessage(e, t))
      else if (isServiceError(e) && e.code === 'offline') setError(t('v2.ds.offline'))
      else setError(paysInApp ? t('v2.legacy.error.tryAgainOrContactUs') : `${t('v2.legacy.preview.labels.availableExtrasView.errorTitle')}. ${t('v2.legacy.preview.labels.availableExtrasView.errorSubtitle')}`)
    } finally {
      setBusy(false)
      setConfirm(false)
    }
  }

  return (
    <>
      <AmountCard
        total={totalText}
        lines={rows.map((r) => ({ label: cartLineLabel(r.extra, r.item.quantity, lang, t), amount: amountText(r.amount, lang, t), ...(r.amount.amount === 0 ? { tone: 'success' as const } : {}) }))}
        buttonLabel={null}
      >
        <button type="button" className="pagos__link-button" onClick={() => go(EXTRAS_ROUTES.catalog)}>
          {t('v2.extras.confirmar.cambiar')}
        </button>
      </AmountCard>

      {needsCard && summary.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={120} />
        </div>
      )}
      {needsCard && summary.kind === 'error' && <SectionError code={summary.code} onRetry={reloadSummary} />}
      {needsCard && s && current && <CardChoice summary={s} value={current} onChange={setChoice} showErrors={showErrors} disabled={busy} />}

      {paysInApp ? (
        <p className="extras__terms">
          <strong>{t('v2.extras.confirmar.condiciones')}</strong> {[...conditions, t('v2.extras.confirmar.recibos')].join(' ')}
        </p>
      ) : (
        <ExplainerBanner>{t('v2.legacy.preview.labels.availableExtrasView.infoMessage1')}</ExplainerBanner>
      )}

      <div className="pagos__group">
        <Button onClick={review} loading={busy} disabled={needsCard && !s}>
          {confirmLabel}
        </Button>
        {error && (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <Sheet
        open={confirm}
        title={paysInApp && total.amount > 0 ? t('v2.pagos.pendiente.confirmarTitulo', { amount: totalText }) : t('v2.extras.confirmar.tituloReal')}
        confirmLabel={confirmLabel}
        onConfirm={() => void submit()}
        onCancel={() => setConfirm(false)}
        loading={busy}
      >
        <p>{t('v2.extras.confirmar.hojaResumen', { names: listJoin(rows.map((r) => r.extra.name), lang), amount: amountText(total, lang, t) })}</p>
        {needsCard && <p>{last4 ? t('v2.pagos.pendiente.confirmarGuardada', { last4 }) : t('v2.pagos.pendiente.confirmarNueva')}</p>}
        {!paysInApp && <p>{t('v2.legacy.preview.labels.availableExtrasView.infoMessage1')}</p>}
      </Sheet>
    </>
  )
}
